import { Slot } from '@radix-ui/react-slot';
import { type VariantProps } from 'class-variance-authority';
import { Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@avanta/shared/utils/cn';
import { FieldIcon } from '../FieldIcon';
import { Label } from '../Label';
import { inputVariants } from './styles';
import { type InputProps } from './types';

const Input = ({
  className,
  variant = 'filled',
  asChild = false,
  label,
  helperText,
  showPassword = false,
  required,
  type = 'text',
  defaultValue,
  leftSlot: LeftSlot,
  rightSlot: RightSlot,
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  value,
  disabled,
  ...props
}: React.ComponentProps<'input'> &
  VariantProps<typeof inputVariants> &
  InputProps): React.ReactElement => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const isHelperTextVisible = !!helperText?.trim();
  const Component = asChild ? Slot : 'input';
  const leftElement = LeftSlot || LeftIcon;
  const rightElement = RightSlot || RightIcon;

  const togglePasswordVisibility = (): void => {
    setIsPasswordVisible(prev => !prev);
  };

  return (
    <div className="flex flex-col gap-2 w-full group/form" data-variant={variant}>
      {label && <Label required={required}>{label}</Label>}
      <div
        className="relative group/input data-[variant=filled]:[--icon-color:var(--color-teal-700)] data-[variant=filled]:[--icon-color-muted:var(--color-teal-600)] data-[variant=invalid]:[--icon-color:var(--color-red-500)] data-[variant=invalid]:[--icon-color-muted:var(--color-red-400)]"
        data-variant={variant}
      >
        {(LeftSlot || LeftIcon) && (
          <div className="absolute left-2 top-1/2 -translate-y-1/2">
            {LeftIcon ? <FieldIcon icon={LeftIcon} /> : LeftSlot}
          </div>
        )}
        <Component
          type={type === 'password' && showPassword && isPasswordVisible ? 'text' : type}
          data-slot="input"
          defaultValue={defaultValue}
          data-has-left-icon={leftElement ? '' : undefined}
          data-has-right-icon={rightElement ? '' : undefined}
          className={cn(
            inputVariants({ variant }),
            'read-only:cursor-default read-only:border-white read-only:bg-mint-100 read-only:text-denim-800 read-only:focus-visible:bg-mint-100',
            className
          )}
          value={value}
          disabled={disabled}
          {...props}
        />
        {showPassword && (
          <button
            type="button"
            tabIndex={-1}
            onClick={togglePasswordVisibility}
            className="absolute right-2 top-1/2 -translate-y-1/2"
          >
            {isPasswordVisible ? <FieldIcon icon={Eye} /> : <FieldIcon icon={EyeOff} />}
          </button>
        )}
        {(RightSlot || RightIcon) && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            {RightIcon ? <FieldIcon icon={RightIcon} /> : RightSlot}
          </div>
        )}
      </div>
      {isHelperTextVisible && (
        <span className="text-xs font-['Inter'] text-gray-500 font-light leading-4.5 min-h-4.5 group-data-[variant=invalid]/form:text-red-500">
          {helperText}
        </span>
      )}
    </div>
  );
};

export { Input, inputVariants, type InputProps };
